import { bound } from "@frui.ts/helpers";
import type { PropertyName } from "@frui.ts/helpers";
import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import { Modules } from "config";
import OutgoingTransferPaymentStatus from "entities/outgoingTransferPaymentStatus";
import TransferType from "entities/transferType";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import OutgoingTransferRepository from "repositories/outgoingTransferRepository";
import DetailViewModel from "../../detailViewModel";
import type { IWithPrevNextNavigationViewModel } from "../../types";
import PagingManagerViewModel from "../../pagingManagerViewModel";
import type OutgoingTransferPaymentDetailsResponse from "entities/outgoingTransferPaymentDetailsResponse";
import type OutgoingBankTransactionDetailResponse from "entities/outgoingBankTransactionDetailResponse";
import type TransferOriginalPaymentDetailResponse from "entities/transferOriginalPaymentDetailResponse";
import type OriginalPayGatePaymentResponse from "entities/originalPayGatePaymentResponse";
import type OutgoingTransferOriginalPaymentResponse from "entities/outgoingTransferOriginalPaymentResponse";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

@Router.registerRoute({ name: Router.Self, route: ":paymentId" })
export default class BankTransferDetailViewModel
  extends DetailViewModel<OutgoingTransferPaymentDetailsResponse>
  implements IWithPrevNextNavigationViewModel
{
  protected readonly modelName = "outgoingTransferPayment";
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable protected itemId: string;

  @computed
  get showSettledFields() {
    return this.item.outgoingPayment.status === OutgoingTransferPaymentStatus.Settled;
  }

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  constructor(
    itemId: string,
    private pagingManager: PagingManagerViewModel<any>,
    private repository: OutgoingTransferRepository,
    public localizationService: LocalizationService,
    private enumService: EnumService,
    private priceListService: PriceListService
  ) {
    super(itemId);
  }

  @watchBusy
  protected loadDetail(): Promise<OutgoingTransferPaymentDetailsResponse> {
    return this.repository.getOutgoingTransferPayment(this.itemId);
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  chargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  @bound
  transferPaymentTypeName(paymentType?: TransferType) {
    return paymentType ? this.localizationService.translateEnum("transferType", paymentType) : "";
  }

  @bound
  close() {
    this.requestClose();
  }

  @computed
  get hasPrevious() {
    return this.pagingManager.hasPrevious;
  }

  @computed
  get hasNext() {
    return this.pagingManager.hasNext;
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const itemId = await this.pagingManager.onPreviousClick();

    if (itemId) {
      this.itemId = itemId;
      this.navigationName = `${this.itemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onNextClick() {
    const itemId = await this.pagingManager.onNextClick();

    if (itemId) {
      this.itemId = itemId;
      this.navigationName = `${itemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `outgoingPayments.${code}`, size);
  }

  translateOutgoingPaymentAttribute(attribute: PropertyName<OutgoingBankTransactionDetailResponse>) {
    return this.localizationService.translateAttribute("outgoingTransferPayment", attribute);
  }

  translateSubAttribute(
    model: "transferOriginalPaymentDetailResponse",
    attribute: PropertyName<TransferOriginalPaymentDetailResponse>
  ): string;
  translateSubAttribute(model: "originalPayGatePaymentResponse", attribute: PropertyName<OriginalPayGatePaymentResponse>): string;
  translateSubAttribute(
    model: "outgoingTransferOriginalPaymentResponse",
    attribute: PropertyName<OutgoingTransferOriginalPaymentResponse>
  ): string;
  translateSubAttribute(model: string, attribute: string) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateOutgoingTransferPaymentStatus(status: OutgoingTransferPaymentStatus) {
    return this.localizationService.translateEnum("outgoingTransferPaymentStatus", status);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, pagingManager: PagingManagerViewModel<any>) =>
      new BankTransferDetailViewModel(
        itemId,
        pagingManager,
        container.get(OutgoingTransferRepository),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(PriceListService)
      );
  }
}
